import { z } from "zod";
import type {
  CalendarEventAttendeeResponseStatus,
  CalendarProvider,
  GraphEntityType
} from "./index";

export const MeetingPreparationSchemaVersion = "salesko.meeting_preparation.v1" as const;
/** Required in addition to agent:provider for the API-to-Agent-only brief call. */
export const MeetingPreparationAgentScope = "meeting_preparation:generate" as const;

export const MeetingPreparationSourceLimits = {
  calendarEvent: 1,
  calendarAttendees: 20,
  graphPeople: 20,
  graphContextNodes: 16,
  graphEdges: 24,
  opportunities: 8,
  evidence: 7,
  totalSources: 96
} as const;

function exhaustiveStringValues<Union extends string>() {
  return <const Values extends readonly [Union, ...Union[]]>(
    values: Exclude<Union, Values[number]> extends never ? Values : never
  ) => values;
}

const MeetingPreparationProviderValues = exhaustiveStringValues<CalendarProvider>()([
  "google",
  "microsoft"
]);
const MeetingPreparationProviderSchema: z.ZodType<CalendarProvider> = z.enum(
  MeetingPreparationProviderValues
);
type MeetingPreparationGraphContextEntityType = Exclude<GraphEntityType, "opportunity">;
const MeetingPreparationGraphEntityTypeValues =
  exhaustiveStringValues<MeetingPreparationGraphContextEntityType>()([
    "person",
    "organisation",
    "event",
    "note"
  ]);
const MeetingPreparationGraphEntityTypeSchema: z.ZodType<MeetingPreparationGraphContextEntityType> =
  z.enum(MeetingPreparationGraphEntityTypeValues);
const MeetingPreparationAttendeeResponseStatusValues =
  exhaustiveStringValues<CalendarEventAttendeeResponseStatus>()([
    "needsAction",
    "declined",
    "tentative",
    "accepted"
  ]);
const MeetingPreparationAttendeeResponseStatusSchema: z.ZodType<CalendarEventAttendeeResponseStatus> =
  z.enum(MeetingPreparationAttendeeResponseStatusValues);

const SourceIdSchema = z.string().min(1).max(160);
const ProviderEventIdSchema = z.string().min(1).max(1_024);
const FrameRecordIdSchema = z.string().min(1).max(160);

function uniqueStringArray<Schema extends z.ZodType<string>>(schema: Schema, maximum: number, minimum = 0) {
  return z
    .array(schema)
    .min(minimum)
    .max(maximum)
    .superRefine((values, ctx) => {
      if (new Set(values).size !== values.length) {
        ctx.addIssue({ code: "custom", message: "Values must be unique." });
      }
    });
}

function collectionBounds<const Limit extends number>(limit: Limit) {
  return z
    .object({
      observed: z.number().int().nonnegative(),
      included: z.number().int().nonnegative(),
      limit: z.literal(limit),
      truncated: z.boolean()
    })
    .strict()
    .superRefine((value, ctx) => {
      if (value.included > value.observed) {
        ctx.addIssue({
          code: "custom",
          message: "included cannot exceed observed.",
          path: ["included"]
        });
      }
      if (value.included > limit) {
        ctx.addIssue({
          code: "custom",
          message: "included cannot exceed the fixed collection limit.",
          path: ["included"]
        });
      }
      if (value.truncated !== (value.observed > value.included)) {
        ctx.addIssue({
          code: "custom",
          message: "truncated must exactly report omitted observed records.",
          path: ["truncated"]
        });
      }
    });
}

export const MeetingPreparationRequestSchema = z
  .object({
    provider: MeetingPreparationProviderSchema,
    providerEventId: ProviderEventIdSchema
  })
  .strict();

export const MeetingPreparationEventSnapshotSchema = z
  .object({
    sourceId: SourceIdSchema,
    provider: MeetingPreparationProviderSchema,
    providerEventId: ProviderEventIdSchema,
    iCalUid: z.string().min(1).max(1_024),
    calendarId: z.string().min(1).max(512),
    summary: z.string().max(500).nullable(),
    location: z.string().max(1_000).nullable(),
    startAt: z.string().datetime(),
    endAt: z.string().datetime().nullable(),
    isRecurring: z.boolean(),
    status: z.enum(["confirmed", "tentative"]),
    organizer: z
      .object({
        email: z.string().email().max(320),
        name: z.string().min(1).max(300).nullable()
      })
      .strict()
      .nullable()
  })
  .strict();

export const MeetingPreparationParticipantSchema = z
  .object({
    calendarSourceId: SourceIdSchema,
    email: z.string().email().max(320),
    name: z.string().min(1).max(300).nullable(),
    responseStatus: MeetingPreparationAttendeeResponseStatusSchema.nullable(),
    optional: z.boolean(),
    graphPersonSourceIds: uniqueStringArray(SourceIdSchema, MeetingPreparationSourceLimits.graphPeople)
  })
  .strict();

const GraphSourceProvenanceShape = {
  frameRecordId: FrameRecordIdSchema,
  frameId: z.string().min(1).max(160),
  acceptedAt: z.string().datetime()
} as const;

export const MeetingPreparationCalendarEventSourceSchema = z
  .object({
    kind: z.literal("calendar_event"),
    sourceId: SourceIdSchema,
    provider: MeetingPreparationProviderSchema,
    providerEventId: ProviderEventIdSchema
  })
  .strict();

export const MeetingPreparationCalendarAttendeeSourceSchema = z
  .object({
    kind: z.literal("calendar_attendee"),
    sourceId: SourceIdSchema,
    provider: MeetingPreparationProviderSchema,
    providerEventId: ProviderEventIdSchema,
    attendeeEmail: z.string().email().max(320)
  })
  .strict();

export const MeetingPreparationGraphNodeSourceSchema = z
  .object({
    kind: z.literal("graph_node"),
    sourceId: SourceIdSchema,
    nodeId: z.string().min(1).max(160),
    canonicalEntityId: z.string().min(1).max(160).nullable(),
    entityType: MeetingPreparationGraphEntityTypeSchema,
    ...GraphSourceProvenanceShape
  })
  .strict();

export const MeetingPreparationGraphEdgeSourceSchema = z
  .object({
    kind: z.literal("graph_edge"),
    sourceId: SourceIdSchema,
    edgeId: z.string().min(1).max(160),
    sourceNodeSourceId: SourceIdSchema,
    targetNodeSourceId: SourceIdSchema,
    ...GraphSourceProvenanceShape
  })
  .strict();

export const MeetingPreparationOpportunitySourceSchema = z
  .object({
    kind: z.literal("opportunity"),
    sourceId: SourceIdSchema,
    opportunityId: z.string().min(1).max(160),
    ...GraphSourceProvenanceShape
  })
  .strict();

export const MeetingPreparationEvidenceSourceSchema = z
  .object({
    kind: z.literal("evidence"),
    sourceId: SourceIdSchema,
    evidenceId: z.string().min(1).max(160),
    subjectSourceId: SourceIdSchema,
    capturedAt: z.string().datetime(),
    ...GraphSourceProvenanceShape
  })
  .strict();

export const MeetingPreparationSourceSchema = z.discriminatedUnion("kind", [
  MeetingPreparationCalendarEventSourceSchema,
  MeetingPreparationCalendarAttendeeSourceSchema,
  MeetingPreparationGraphNodeSourceSchema,
  MeetingPreparationGraphEdgeSourceSchema,
  MeetingPreparationOpportunitySourceSchema,
  MeetingPreparationEvidenceSourceSchema
]);

const CitedSourceIdsSchema = uniqueStringArray(SourceIdSchema, 16, 1);

export const MeetingPreparationCitedTextSchema = z
  .object({
    text: z.string().min(1).max(4_000),
    sourceIds: CitedSourceIdsSchema
  })
  .strict();

export const MeetingPreparationParticipantContextSchema = z
  .object({
    participantSourceId: SourceIdSchema,
    text: z.string().min(1).max(2_000),
    sourceIds: CitedSourceIdsSchema
  })
  .strict();

export const MeetingPreparationBriefSchema = z
  .object({
    goal: MeetingPreparationCitedTextSchema,
    knownContext: z.array(MeetingPreparationCitedTextSchema).max(12),
    participantContext: z.array(MeetingPreparationParticipantContextSchema).max(20),
    relationshipSignals: z.array(MeetingPreparationCitedTextSchema).max(12),
    watchouts: z.array(MeetingPreparationCitedTextSchema).max(10),
    suggestedQuestions: z.array(MeetingPreparationCitedTextSchema).min(1).max(10),
    suggestedClose: MeetingPreparationCitedTextSchema
  })
  .strict();

export const MeetingPreparationTruncatedCollectionSchema = z.enum([
  "calendarAttendees",
  "graphPeople",
  "graphContextNodes",
  "graphEdges",
  "opportunities",
  "evidence"
]);

const TruncatedCollectionsSchema = uniqueStringArray(
  MeetingPreparationTruncatedCollectionSchema,
  6,
  1
).superRefine((values, ctx) => {
  const sorted = [...values].sort();
  if (values.some((value, index) => value !== sorted[index])) {
    ctx.addIssue({ code: "custom", message: "Truncated collections must be sorted." });
  }
});

export const MeetingPreparationGapSchema = z.discriminatedUnion("code", [
  z
    .object({
      code: z.literal("no_graph_person_match"),
      participantSourceId: SourceIdSchema
    })
    .strict(),
  z.object({ code: z.literal("no_relationship_evidence") }).strict(),
  z.object({ code: z.literal("no_opportunity_evidence") }).strict(),
  z
    .object({
      code: z.literal("context_truncated"),
      collections: TruncatedCollectionsSchema
    })
    .strict(),
  z
    .object({
      code: z.literal("optional_calendar_detail_unavailable"),
      detail: z.enum(["summary", "location", "end_at", "organizer"])
    })
    .strict()
]);

export const MeetingPreparationBoundsSchema = z
  .object({
    calendarEvent: collectionBounds(MeetingPreparationSourceLimits.calendarEvent),
    calendarAttendees: collectionBounds(MeetingPreparationSourceLimits.calendarAttendees),
    graphPeople: collectionBounds(MeetingPreparationSourceLimits.graphPeople),
    graphContextNodes: collectionBounds(MeetingPreparationSourceLimits.graphContextNodes),
    graphEdges: collectionBounds(MeetingPreparationSourceLimits.graphEdges),
    opportunities: collectionBounds(MeetingPreparationSourceLimits.opportunities),
    evidence: collectionBounds(MeetingPreparationSourceLimits.evidence),
    totalSources: collectionBounds(MeetingPreparationSourceLimits.totalSources)
  })
  .strict()
  .superRefine((value, ctx) => {
    const categories = [
      value.calendarEvent,
      value.calendarAttendees,
      value.graphPeople,
      value.graphContextNodes,
      value.graphEdges,
      value.opportunities,
      value.evidence
    ];
    const included = categories.reduce((sum, item) => sum + item.included, 0);
    const observed = categories.reduce((sum, item) => sum + item.observed, 0);
    if (value.calendarEvent.observed !== 1 || value.calendarEvent.included !== 1) {
      ctx.addIssue({
        code: "custom",
        message: "A successful response must contain exactly one Calendar event source.",
        path: ["calendarEvent"]
      });
    }
    if (value.totalSources.included !== included) {
      ctx.addIssue({
        code: "custom",
        message: "totalSources.included must equal the sum of included categories.",
        path: ["totalSources", "included"]
      });
    }
    if (value.totalSources.observed !== observed) {
      ctx.addIssue({
        code: "custom",
        message: "totalSources.observed must equal the sum of observed categories.",
        path: ["totalSources", "observed"]
      });
    }
  });

export const MeetingPreparationGraphContextSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("available"),
      sourceFrameRecordIds: uniqueStringArray(
        FrameRecordIdSchema,
        MeetingPreparationSourceLimits.totalSources,
        1
      )
    })
    .strict(),
  z
    .object({
      status: z.literal("unavailable"),
      reason: z.enum(["event_not_in_graph", "canonical_entity_not_found"]),
      sourceFrameRecordIds: z.tuple([])
    })
    .strict()
]);

export const MeetingPreparationGenerationSchema = z
  .object({
    status: z.literal("generated"),
    generatedAt: z.string().datetime(),
    durationMs: z.number().int().nonnegative()
  })
  .strict();

/**
 * The API-to-Agent boundary carries a bounded, display-safe rendering of each
 * selected source. It is deliberately separate from the public source record:
 * source metadata proves provenance to callers, while facts give the sole
 * semantic producer enough already-authorized text to write the brief. No
 * tenant, connection, credential, raw provider payload, or arbitrary graph
 * data crosses this contract.
 */
export const MeetingPreparationGenerationSourceFactSchema = z
  .object({
    sourceId: SourceIdSchema,
    text: z.string().min(1).max(2_000)
  })
  .strict();

export const MeetingPreparationGenerationRequestSchema = z
  .object({
    schemaVersion: z.literal(MeetingPreparationSchemaVersion),
    event: MeetingPreparationEventSnapshotSchema,
    participants: z
      .array(MeetingPreparationParticipantSchema)
      .min(1)
      .max(MeetingPreparationSourceLimits.calendarAttendees),
    sources: z.array(MeetingPreparationSourceSchema).min(1).max(MeetingPreparationSourceLimits.totalSources),
    sourceFacts: z
      .array(MeetingPreparationGenerationSourceFactSchema)
      .min(1)
      .max(MeetingPreparationSourceLimits.totalSources),
    gaps: z.array(MeetingPreparationGapSchema).max(32),
    bounds: MeetingPreparationBoundsSchema,
    graphContext: MeetingPreparationGraphContextSchema
  })
  .strict()
  .superRefine((value, ctx) => {
    const sourceIds = value.sources.map((source) => source.sourceId);
    const sourceFactIds = value.sourceFacts.map((sourceFact) => sourceFact.sourceId);
    if (new Set(sourceFactIds).size !== sourceFactIds.length) {
      ctx.addIssue({
        code: "custom",
        message: "Generation source facts must use unique source ids.",
        path: ["sourceFacts"]
      });
    }
    if (!sameStrings(sourceFactIds, sourceIds)) {
      ctx.addIssue({
        code: "custom",
        message: "Generation source facts must exactly match the supplied source order.",
        path: ["sourceFacts"]
      });
    }
  });

export const MeetingPreparationGenerationResponseSchema = z
  .object({
    brief: MeetingPreparationBriefSchema
  })
  .strict();

function sourceCategory(source: z.infer<typeof MeetingPreparationSourceSchema>) {
  switch (source.kind) {
    case "calendar_event":
      return "calendarEvent" as const;
    case "calendar_attendee":
      return "calendarAttendees" as const;
    case "graph_node":
      return source.entityType === "person" ? ("graphPeople" as const) : ("graphContextNodes" as const);
    case "graph_edge":
      return "graphEdges" as const;
    case "opportunity":
      return "opportunities" as const;
    case "evidence":
      return "evidence" as const;
  }
}

function sameStrings(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export const MeetingPreparationResponseSchema = z
  .object({
    schemaVersion: z.literal(MeetingPreparationSchemaVersion),
    event: MeetingPreparationEventSnapshotSchema,
    participants: z
      .array(MeetingPreparationParticipantSchema)
      .min(1)
      .max(MeetingPreparationSourceLimits.calendarAttendees),
    brief: MeetingPreparationBriefSchema,
    sources: z.array(MeetingPreparationSourceSchema).min(1).max(MeetingPreparationSourceLimits.totalSources),
    gaps: z.array(MeetingPreparationGapSchema).max(32),
    bounds: MeetingPreparationBoundsSchema,
    graphContext: MeetingPreparationGraphContextSchema,
    generation: MeetingPreparationGenerationSchema
  })
  .strict()
  .superRefine((value, ctx) => {
    const sourceById = new Map(value.sources.map((source) => [source.sourceId, source]));
    if (sourceById.size !== value.sources.length) {
      ctx.addIssue({ code: "custom", message: "Source ids must be unique.", path: ["sources"] });
    }

    const eventSource = sourceById.get(value.event.sourceId);
    if (
      eventSource?.kind !== "calendar_event" ||
      eventSource.provider !== value.event.provider ||
      eventSource.providerEventId !== value.event.providerEventId
    ) {
      ctx.addIssue({
        code: "custom",
        message: "The event source must identify the same provider event.",
        path: ["event", "sourceId"]
      });
    }

    const participantSourceIds = new Set<string>();
    const unmatchedParticipantSourceIds = new Set<string>();
    const organizerEmail = value.event.organizer?.email.toLowerCase() ?? null;
    let hasNonDeclinedExternalParticipant = false;
    value.participants.forEach((participant, participantIndex) => {
      if (participantSourceIds.has(participant.calendarSourceId)) {
        ctx.addIssue({
          code: "custom",
          message: "Each participant must use a distinct Calendar attendee source.",
          path: ["participants", participantIndex, "calendarSourceId"]
        });
      }
      participantSourceIds.add(participant.calendarSourceId);
      const participantEmail = participant.email.toLowerCase();
      if (organizerEmail !== null && participantEmail === organizerEmail) {
        ctx.addIssue({
          code: "custom",
          message: "Participants must exclude the Calendar event organizer.",
          path: ["participants", participantIndex, "email"]
        });
      }
      if (participant.responseStatus !== "declined" && participantEmail !== organizerEmail) {
        hasNonDeclinedExternalParticipant = true;
      }
      const calendarSource = sourceById.get(participant.calendarSourceId);
      if (
        calendarSource?.kind !== "calendar_attendee" ||
        calendarSource.provider !== value.event.provider ||
        calendarSource.providerEventId !== value.event.providerEventId ||
        calendarSource.attendeeEmail.toLowerCase() !== participant.email.toLowerCase()
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Participant Calendar source does not match the participant or event.",
          path: ["participants", participantIndex, "calendarSourceId"]
        });
      }
      if (participant.graphPersonSourceIds.length === 0) {
        unmatchedParticipantSourceIds.add(participant.calendarSourceId);
      }
      participant.graphPersonSourceIds.forEach((sourceId, sourceIndex) => {
        const graphSource = sourceById.get(sourceId);
        if (graphSource?.kind !== "graph_node" || graphSource.entityType !== "person") {
          ctx.addIssue({
            code: "custom",
            message: "graphPersonSourceIds must reference supplied graph person sources.",
            path: ["participants", participantIndex, "graphPersonSourceIds", sourceIndex]
          });
        }
      });
    });
    if (!hasNonDeclinedExternalParticipant) {
      ctx.addIssue({
        code: "custom",
        message: "A successful response requires a non-declined external participant.",
        path: ["participants"]
      });
    }

    const provenanceByFrameRecordId = new Map<string, { frameId: string; acceptedAt: string }>();
    value.sources.forEach((source, sourceIndex) => {
      if ("frameRecordId" in source) {
        const existingProvenance = provenanceByFrameRecordId.get(source.frameRecordId);
        if (
          existingProvenance &&
          (existingProvenance.frameId !== source.frameId || existingProvenance.acceptedAt !== source.acceptedAt)
        ) {
          ctx.addIssue({
            code: "custom",
            message: "Graph sources sharing a frameRecordId must use the same frameId and acceptedAt.",
            path: ["sources", sourceIndex, "frameRecordId"]
          });
        } else if (!existingProvenance) {
          provenanceByFrameRecordId.set(source.frameRecordId, {
            frameId: source.frameId,
            acceptedAt: source.acceptedAt
          });
        }
      }

      if (source.kind === "graph_edge") {
        for (const [field, sourceId] of [
          ["sourceNodeSourceId", source.sourceNodeSourceId],
          ["targetNodeSourceId", source.targetNodeSourceId]
        ] as const) {
          const endpoint = sourceById.get(sourceId);
          if (endpoint?.kind !== "graph_node" && endpoint?.kind !== "opportunity") {
            ctx.addIssue({
              code: "custom",
              message: "Graph edge endpoints must reference supplied graph node or opportunity sources.",
              path: ["sources", sourceIndex, field]
            });
          } else if (endpoint.frameRecordId !== source.frameRecordId) {
            ctx.addIssue({
              code: "custom",
              message: "Graph edge endpoints must come from the edge's committed frame.",
              path: ["sources", sourceIndex, field]
            });
          }
        }
      }
      if (source.kind === "evidence") {
        const subject = sourceById.get(source.subjectSourceId);
        if (
          subject?.kind !== "graph_node" &&
          subject?.kind !== "graph_edge" &&
          subject?.kind !== "opportunity"
        ) {
          ctx.addIssue({
            code: "custom",
            message: "Evidence must reference a supplied graph node, edge, or opportunity source.",
            path: ["sources", sourceIndex, "subjectSourceId"]
          });
        }
      }
    });

    const citedSourceIds: Array<{ path: (string | number)[]; sourceId: string }> = [];
    const addCitations = (path: (string | number)[], sourceIds: string[]) => {
      sourceIds.forEach((sourceId, index) => citedSourceIds.push({ path: [...path, "sourceIds", index], sourceId }));
    };
    addCitations(["brief", "goal"], value.brief.goal.sourceIds);
    value.brief.knownContext.forEach((item, index) => addCitations(["brief", "knownContext", index], item.sourceIds));
    value.brief.participantContext.forEach((item, index) => {
      if (sourceById.get(item.participantSourceId)?.kind !== "calendar_attendee") {
        ctx.addIssue({
          code: "custom",
          message: "Participant context must reference a supplied Calendar attendee source.",
          path: ["brief", "participantContext", index, "participantSourceId"]
        });
      }
      addCitations(["brief", "participantContext", index], item.sourceIds);
    });
    value.brief.relationshipSignals.forEach((item, index) =>
      addCitations(["brief", "relationshipSignals", index], item.sourceIds)
    );
    value.brief.watchouts.forEach((item, index) => addCitations(["brief", "watchouts", index], item.sourceIds));
    value.brief.suggestedQuestions.forEach((item, index) =>
      addCitations(["brief", "suggestedQuestions", index], item.sourceIds)
    );
    addCitations(["brief", "suggestedClose"], value.brief.suggestedClose.sourceIds);
    citedSourceIds.forEach(({ path, sourceId }) => {
      if (!sourceById.has(sourceId)) {
        ctx.addIssue({ code: "custom", message: "Citation is outside the supplied source allowlist.", path });
      }
    });

    const noMatchGapIds = value.gaps
      .filter((gap): gap is Extract<(typeof value.gaps)[number], { code: "no_graph_person_match" }> =>
        gap.code === "no_graph_person_match"
      )
      .map((gap) => gap.participantSourceId)
      .sort();
    const expectedNoMatchGapIds = [...unmatchedParticipantSourceIds].sort();
    if (!sameStrings(noMatchGapIds, expectedNoMatchGapIds)) {
      ctx.addIssue({
        code: "custom",
        message: "no_graph_person_match gaps must exactly cover unmatched participants.",
        path: ["gaps"]
      });
    }

    const optionalDetailGaps = value.gaps
      .filter(
        (
          gap
        ): gap is Extract<
          (typeof value.gaps)[number],
          { code: "optional_calendar_detail_unavailable" }
        > => gap.code === "optional_calendar_detail_unavailable"
      )
      .map((gap) => gap.detail)
      .sort();
    const unavailableOptionalDetails = [
      ...(value.event.summary === null ? (["summary"] as const) : []),
      ...(value.event.location === null ? (["location"] as const) : []),
      ...(value.event.endAt === null ? (["end_at"] as const) : []),
      ...(value.event.organizer === null ? (["organizer"] as const) : [])
    ].sort();
    if (!sameStrings(optionalDetailGaps, unavailableOptionalDetails)) {
      ctx.addIssue({
        code: "custom",
        message: "Optional Calendar detail gaps must exactly match null event fields.",
        path: ["gaps"]
      });
    }

    const counts = {
      calendarEvent: 0,
      calendarAttendees: 0,
      graphPeople: 0,
      graphContextNodes: 0,
      graphEdges: 0,
      opportunities: 0,
      evidence: 0
    };
    value.sources.forEach((source) => {
      counts[sourceCategory(source)] += 1;
    });
    for (const category of Object.keys(counts) as Array<keyof typeof counts>) {
      if (value.bounds[category].included !== counts[category]) {
        ctx.addIssue({
          code: "custom",
          message: "Bounds included count does not match supplied sources.",
          path: ["bounds", category, "included"]
        });
      }
    }
    if (value.bounds.totalSources.included !== value.sources.length) {
      ctx.addIssue({
        code: "custom",
        message: "totalSources.included does not match supplied sources.",
        path: ["bounds", "totalSources", "included"]
      });
    }
    if (value.participants.length !== counts.calendarAttendees) {
      ctx.addIssue({
        code: "custom",
        message: "Participants must exactly match included Calendar attendee sources.",
        path: ["participants"]
      });
    }

    const graphFrameRecordIds = [
      ...new Set(
        value.sources.flatMap((source) =>
          "frameRecordId" in source ? [source.frameRecordId] : []
        )
      )
    ].sort();
    if (!sameStrings(value.graphContext.sourceFrameRecordIds, graphFrameRecordIds)) {
      ctx.addIssue({
        code: "custom",
        message: "sourceFrameRecordIds must equal the sorted distinct graph source frameRecordIds.",
        path: ["graphContext", "sourceFrameRecordIds"]
      });
    }
    if (
      (graphFrameRecordIds.length === 0 && value.graphContext.status !== "unavailable") ||
      (graphFrameRecordIds.length > 0 && value.graphContext.status !== "available")
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Graph context status must agree with supplied graph sources.",
        path: ["graphContext", "status"]
      });
    }

    const truncatedCategories = (
      [
        "calendarAttendees",
        "graphPeople",
        "graphContextNodes",
        "graphEdges",
        "opportunities",
        "evidence"
      ] as const
    )
      .filter((category) => value.bounds[category].truncated)
      .sort();
    const truncationGaps = value.gaps.filter(
      (gap): gap is Extract<(typeof value.gaps)[number], { code: "context_truncated" }> =>
        gap.code === "context_truncated"
    );
    if (
      (truncatedCategories.length === 0 && truncationGaps.length !== 0) ||
      (truncatedCategories.length > 0 &&
        (truncationGaps.length !== 1 || !sameStrings(truncationGaps[0]?.collections ?? [], truncatedCategories)))
    ) {
      ctx.addIssue({
        code: "custom",
        message: "context_truncated must exactly report truncated categories.",
        path: ["gaps"]
      });
    }

    const hasRelationshipGap = value.gaps.some((gap) => gap.code === "no_relationship_evidence");
    if (hasRelationshipGap !== (counts.graphEdges === 0)) {
      ctx.addIssue({
        code: "custom",
        message: "no_relationship_evidence must exactly report an empty graph-edge set.",
        path: ["gaps"]
      });
    }
    const hasOpportunityGap = value.gaps.some((gap) => gap.code === "no_opportunity_evidence");
    if (hasOpportunityGap !== (counts.opportunities === 0)) {
      ctx.addIssue({
        code: "custom",
        message: "no_opportunity_evidence must exactly report an empty opportunity set.",
        path: ["gaps"]
      });
    }
  });

export const MeetingPreparationErrorCodeSchema = z.enum([
  "meeting_preparation_request_invalid",
  "meeting_preparation_auth_required",
  "meeting_preparation_entitlement_required",
  "meeting_preparation_event_not_found",
  "meeting_preparation_event_stale",
  "meeting_preparation_event_not_eligible",
  "meeting_preparation_graph_read_failed",
  "meeting_preparation_graph_context_invalid",
  "meeting_preparation_model_unavailable",
  "meeting_preparation_model_timeout",
  "meeting_preparation_invalid_model_output",
  "meeting_preparation_unsupported_source"
]);

export const MeetingPreparationErrorRetryability = {
  meeting_preparation_request_invalid: false,
  meeting_preparation_auth_required: false,
  meeting_preparation_entitlement_required: false,
  meeting_preparation_event_not_found: false,
  meeting_preparation_event_stale: false,
  meeting_preparation_event_not_eligible: false,
  meeting_preparation_graph_read_failed: true,
  meeting_preparation_graph_context_invalid: false,
  meeting_preparation_model_unavailable: true,
  meeting_preparation_model_timeout: true,
  meeting_preparation_invalid_model_output: false,
  meeting_preparation_unsupported_source: false
} as const satisfies Record<z.infer<typeof MeetingPreparationErrorCodeSchema>, boolean>;

export const MeetingPreparationErrorResponseSchema = z
  .object({
    error: z
      .object({
        code: MeetingPreparationErrorCodeSchema,
        message: z.string().min(1).max(300),
        retryable: z.boolean()
      })
      .strict()
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.error.retryable !== MeetingPreparationErrorRetryability[value.error.code]) {
      ctx.addIssue({
        code: "custom",
        message: "retryable must match the canonical error policy.",
        path: ["error", "retryable"]
      });
    }
  });

export type MeetingPreparationRequest = z.infer<typeof MeetingPreparationRequestSchema>;
export type MeetingPreparationEventSnapshot = z.infer<typeof MeetingPreparationEventSnapshotSchema>;
export type MeetingPreparationParticipant = z.infer<typeof MeetingPreparationParticipantSchema>;
export type MeetingPreparationSource = z.infer<typeof MeetingPreparationSourceSchema>;
export type MeetingPreparationBrief = z.infer<typeof MeetingPreparationBriefSchema>;
export type MeetingPreparationGap = z.infer<typeof MeetingPreparationGapSchema>;
export type MeetingPreparationBounds = z.infer<typeof MeetingPreparationBoundsSchema>;
export type MeetingPreparationGraphContext = z.infer<typeof MeetingPreparationGraphContextSchema>;
export type MeetingPreparationGeneration = z.infer<typeof MeetingPreparationGenerationSchema>;
export type MeetingPreparationGenerationSourceFact = z.infer<typeof MeetingPreparationGenerationSourceFactSchema>;
export type MeetingPreparationGenerationRequest = z.infer<typeof MeetingPreparationGenerationRequestSchema>;
export type MeetingPreparationGenerationResponse = z.infer<typeof MeetingPreparationGenerationResponseSchema>;
export type MeetingPreparationResponse = z.infer<typeof MeetingPreparationResponseSchema>;
export type MeetingPreparationErrorCode = z.infer<typeof MeetingPreparationErrorCodeSchema>;
export type MeetingPreparationErrorResponse = z.infer<typeof MeetingPreparationErrorResponseSchema>;
